import SectionHeader from "@/components/SectionHeader";
import MarketSnapshot from "@/components/MarketSnapshot";
import { IMG } from "@/lib/api";
import { Link } from "react-router-dom";
import { Search, ArrowUpRight } from "lucide-react";

const HOODS = [
  {
    name: "Malibu",
    blurb: "Ocean-front sanctuary. For those who measure success in low tide.",
    img: IMG.lifestyle1,
    span: "md:col-span-7 md:row-span-2",
    h: "h-[420px] md:h-full",
  },
  {
    name: "Santa Monica",
    blurb: "Coastal-meets-cosmopolitan. Bikes, brunch, and big windows.",
    img: IMG.lifestyle2,
    span: "md:col-span-5",
    h: "h-[260px]",
  },
  {
    name: "Hollywood Hills",
    blurb: "Where canyon roads meet city lights.",
    img: IMG.interior1,
    span: "md:col-span-5",
    h: "h-[260px]",
  },
  {
    name: "Venice",
    blurb: "Creative, salty, walkable. The original LA cool.",
    img: IMG.interior2,
    span: "md:col-span-6",
    h: "h-[320px]",
  },
  {
    name: "Beverly Hills",
    blurb: "Quiet streets, oak-lined estates, generational legacy.",
    img: IMG.lifestyle1,
    span: "md:col-span-6",
    h: "h-[320px]",
  },
  {
    name: "Manhattan Beach",
    blurb: "South Bay sunlight. Where the surf culture grew up nicely.",
    img: IMG.lifestyle2,
    span: "md:col-span-7",
    h: "h-[320px]",
  },
  {
    name: "Pacific Palisades",
    blurb: "Bluffs, parks, and a slower, sun-soaked pace.",
    img: IMG.interior2,
    span: "md:col-span-5",
    h: "h-[320px]",
  },
];

export default function Lifestyles() {
  return (
    <div data-testid="lifestyles-page">
      <section className="container-edge pt-16 pb-8">
        <p className="overline">Lifestyles</p>
        <h1 className="mt-6 text-5xl md:text-6xl lg:text-7xl max-w-4xl" style={{ fontFamily: "Playfair Display, serif" }}>
          There's an LA for everyone. Find yours.
        </h1>
        <p className="mt-6 text-soft max-w-3xl leading-relaxed">
          Los Angeles isn't one place or one lifestyle. It's a hundred neighborhoods, each with
          its own rhythm, personality, and way of living. Where you live changes how you live —
          so don't just search for a home. Find the neighborhood that feels like you, the
          lifestyle that fits you, and the corner of LA that feels like home. Your LA starts here.
        </p>
      </section>

      <section className="section pt-12" data-testid="lifestyles-search-cta">
        <div className="bg-paper border border-soft p-10 md:p-14 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <p className="overline flex items-center gap-2"><Search size={14} className="text-sand" /> Start your search</p>
            <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl" style={{ fontFamily: "Playfair Display, serif" }}>
              What's your home worth today?
            </h2>
            <p className="mt-4 text-soft max-w-xl">
              Get an instant LA market estimate — backed by real comps and refined with our local
              insight. Free, no obligation.
            </p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link to="/home-value" className="btn-primary inline-flex" data-testid="lifestyles-search-cta-btn">
              Search your home value <ArrowUpRight size={14} className="ml-2" />
            </Link>
          </div>
        </div>
      </section>

      <section className="section pt-12" data-testid="lifestyles-market">
        <MarketSnapshot />
      </section>

      <section className="section pt-0" data-testid="lifestyles-grid">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
          {HOODS.map((h, i) => (
            <Link
              key={h.name}
              to="/contact"
              className={`relative zoom-img overflow-hidden group ${h.span} ${h.h}`}
              data-testid={`hood-${h.name.toLowerCase().replace(/\s/g, "-")}`}
            >
              <img src={h.img} alt={h.name} className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/70 to-black/35" />
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-white">
                <h3
                  style={{
                    fontFamily: "Playfair Display, serif",
                    fontWeight: 400,
                    textShadow: "0 2px 6px rgba(0,0,0,0.95), 0 6px 30px rgba(0,0,0,0.8)",
                    color: "#ffffff",
                  }}
                  className="text-4xl md:text-5xl tracking-wide"
                >
                  {h.name}
                </h3>
                <p className="mt-3 text-sm md:text-base text-white max-w-md font-light" style={{ textShadow: "0 1px 4px rgba(0,0,0,0.85)" }}>{h.blurb}</p>
                <span className="mt-4 inline-block overline" style={{ color: "#f4d9a8", textShadow: "0 1px 3px rgba(0,0,0,0.9)" }}>
                  Inquire →
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="section bg-linen">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <SectionHeader
              overline="Not seeing yours?"
              title="We cover all of greater LA."
              subtitle="From the South Bay to the foothills — let's talk about what life you want, and where in LA it lives."
            />
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link to="/contact" className="btn-primary" data-testid="lifestyles-final-cta">Start a search</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
